import { Card, Form, Button } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getRelativePosition } from 'chart.js/helpers';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AREA_CODES, areaColor } from "../constants";
import { useCurrency } from "../../../currency/CurrencyProvider";
import { AreaPicker } from "../AreaPicker";
import { BarChart, Cloud, Cpu, Star } from "../../icons";
import { useLanguage } from "../../../i18n/LanguageProvider";
import { track } from "../../../utils/track";
import { useApi } from "../apiService";
import { forecastAreaPlugin, nowLinePlugin, predictionHoverPlugin } from "./chartPlugins";
import {
  getPredictionCategory,
  darkenRgba,
  lightenRgba,
  getCommodityColor,
  calculateWeightedPredictionElectricity,
  calculateWeightedPredictionCommodity,
  calculateTimeSpan,
  aggregatePricesTo4Hourly,
} from "./chartUtils";
import { ChartProps, Prediction } from "./chartTypes";
import { useUrlState } from "../../../hooks/useUrlState";
import './chart.css';

// Helper to detect mobile devices
const isMobile = () => window.innerWidth < 768;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
ChartJS.register(zoomPlugin);
ChartJS.register(forecastAreaPlugin, nowLinePlugin, predictionHoverPlugin);

// Swedish electricity spot prices are inherently in Swedish wall-clock time, so we
// always render price/forecast timestamps in Europe/Stockholm regardless of the
// viewer's browser timezone (otherwise e.g. a UTC+1 browser shows the 23:00
// day-ahead hour as 22:00, and visitors abroad are shifted further).
const SWEDISH_TZ = "Europe/Stockholm";

// Balanced defaults kept only for the legacy-row fallback blend (new rows are
// the single unified type and never hit it); no longer user-adjustable.
// MODULE scope on purpose: these sit in updateChartData's dependency array, and
// as component-body literals they'd be a fresh reference every render → the
// [rawPredictions, updateChartData] effect refires → setPredictionsData(new
// object) → re-render → repeat. That infinite update loop (with a Chart.js
// animation per iteration) pegged the CPU whenever the chart page was open.
const sliderValue = 5; // 0-10 scale, middle
const triangleWeights = { weather: 0.33, regression: 0.33, news: 0.34 };

const Chart = ({ rawPredictions, loadingPredictions, prices, loadingPrices, commodity = 'electricity', availableTimestamps, loadingTimestamps, selectedGeneratedAt, onGeneratedAtChange, forecastRange, onForecastRangeChange, canViewExtended, selectedArea: controlledArea, onAreaChange, validationMode }: ChartProps) => {
  const { t, locale, language } = useLanguage();
  const { unit, convert } = useCurrency();
  const api = useApi();
  const { getParam, setParam } = useUrlState();
  // Time-travel (date/hour picker + exact-interval window) is driven entirely by
  // the caller via `validationMode` — only the admin Prognosnoggrannhet tab sets
  // it. The customer start page never enables it, so there is no way to view past
  // generations there for any role.
  const timeTravel = validationMode === true;
  // forecastRange / canViewExtended are owned by home.tsx (URL-synced) and the
  // server returns daily-aggregated data for 30d, so the chart just renders it.
  const isExtended = forecastRange === '30d' && canViewExtended;
  
  // Dark mode detection - listen for theme changes
  const [darkMode, setDarkMode] = useState(() => document.documentElement.getAttribute('data-bs-theme') === 'dark');
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.getAttribute('data-bs-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  const [predictionsData, setPredictionsData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const chartRef = useRef<any>(null);
  // Selected area (ALL or one of AREA_CODES) - only relevant for electricity.
  // Controlled by the parent (home.tsx, URL-synced + persisted) when provided;
  // otherwise falls back to internal state (admin validation view).
  const [internalArea, setInternalArea] = useState<string>('ALL');
  const selectedArea = controlledArea ?? internalArea;
  const setSelectedArea = onAreaChange ?? setInternalArea;

  // Store the prediction category based on current asset type
  // Categories: 'electricity', 'commodity' (copper, crude), 'stock' (tesla, nvidia, etc.)
  const predictionCategory = useMemo(() => getPredictionCategory(commodity), [commodity]);
  
  // URL-synced state for prediction reasoning modal
  const urlPredictionId = getParam('prediction');
  
  // Reasoning modal state
  const [reasoningData, setReasoningData] = useState<{
    text: string;
    x: number;
    y: number;
    predictionType: 'news' | 'weather' | 'regression';
    timestamp: string;
    predictedValue: number;
    generatedAt?: string;
    predictionId?: string;
  } | null>(null);
  const [reasoningMap, setReasoningMap] = useState<Map<string, { reasoning: string; predictionId: string }>>(new Map());
  const reasoningMapRef = useRef<Map<string, { reasoning: string; predictionId: string }>>(new Map());
  
  // Show reasoning modal when URL has prediction param (like DocumentModal does)
  const showReasoning = Boolean(urlPredictionId);
  
  const closeReasoningModal = useCallback(() => {
    setParam('prediction', null);
    setReasoningData(null);
  }, [setParam]);
  
  const openReasoningModal = useCallback((predictionId: string, data: typeof reasoningData) => {
    setReasoningData(data);
    setParam('prediction', predictionId);
  }, [setParam]);

  // Handle deep linking: load prediction reasoning when URL has prediction ID
  useEffect(() => {
    if (urlPredictionId) {
      if (!reasoningData || String(reasoningData.predictionId) !== String(urlPredictionId)) {
        let foundPrediction = null;
        for (const areaKey in rawPredictions) {
          const prediction = rawPredictions[areaKey].find(p => String(p.id) === String(urlPredictionId));
          if (prediction) {
            foundPrediction = prediction;
            break;
          }
        }

        if (foundPrediction) {
          const reasoningInfo = reasoningMap.get(String(urlPredictionId));
          const reasoningText = reasoningInfo?.reasoning || foundPrediction.reasoning;

          if (reasoningText) {
            const predictionType: 'news' | 'weather' | 'regression' =
              foundPrediction.type === 'weather' ? 'weather' :
              foundPrediction.type === 'regression' ? 'regression' : 'news';

            setReasoningData({
              text: reasoningText,
              x: 0,
              y: 0,
              predictionType,
              timestamp: foundPrediction.targetTime,
              predictedValue: foundPrediction.predictedValue,
              generatedAt: foundPrediction.generatedAt,
              predictionId: urlPredictionId,
            });
          }
        } else if (Object.keys(rawPredictions).length > 0) {
          api.fetchPredictionById(urlPredictionId)
            .then(response => {
              if (response.prediction && response.prediction.reasoning) {
                const pred = response.prediction;
                setReasoningData({
                  text: pred.reasoning!,
                  x: 0,
                  y: 0,
                  predictionType: 'news',
                  timestamp: pred.targetTime,
                  predictedValue: pred.predictedValue,
                  generatedAt: pred.generatedAt,
                  predictionId: urlPredictionId,
                });
              } else {
                setParam('prediction', null);
              }
            })
            .catch(err => {
              console.error('Error fetching prediction:', err);
              setParam('prediction', null);
            });
        }
      }
    } else if (!urlPredictionId && reasoningData) {
      setReasoningData(null);
    }
  }, [urlPredictionId, rawPredictions, reasoningMap, reasoningData, api, setParam]);

  // Handle chart click for showing reasoning on system predictions.
  const handleChartClick = useCallback((event: any, elements: any[]) => {
    if (!chartRef.current || !elements || elements.length === 0) return;

    const element = elements[0];
    if (!element) return;

    const chart = chartRef.current;
    const dataset = chart.data.datasets[element.datasetIndex];
    if (!dataset.metadata) return;

    const { timestamps, predictionIds } = dataset.metadata;
    const timestamp = timestamps[element.index];
    const predictionId = predictionIds?.[element.index];

    if (dataset.data[element.index] === null || dataset.data[element.index] === undefined) return;
    if (!predictionId) return;

    const reasoningData = reasoningMapRef.current.get(predictionId);
    if (!reasoningData) return;

    const canvasPosition = getRelativePosition(event, chart);
    let generatedAt: string | undefined;
    for (const areaKey in rawPredictions) {
      const prediction = rawPredictions[areaKey].find(p => String(p.id) === String(predictionId));
      if (prediction) {
        generatedAt = prediction.generatedAt;
        break;
      }
    }

    openReasoningModal(predictionId, {
      text: reasoningData.reasoning,
      x: canvasPosition.x,
      y: canvasPosition.y,
      predictionType: 'news',
      timestamp,
      predictedValue: dataset.data[element.index],
      generatedAt,
      predictionId,
    });
  }, [rawPredictions, openReasoningModal]);


  // Derive datepicker values from availableTimestamps
  const timestampsByDate = useMemo(() => {
    const map = new Map<string, string[]>();
    availableTimestamps.forEach(ts => {
      const date = ts.substring(0, 10);
      if (!map.has(date)) map.set(date, []);
      map.get(date)!.push(ts);
    });
    return map;
  }, [availableTimestamps]);

  // Derive a few read-only stats about the currently-loaded forecast so the
  // control panel still has something meaningful in it for users who can't see
  // the admin datepicker. Computed from rawPredictions to avoid extra fetches.
  const forecastStats = useMemo(() => {
    const allPreds: Prediction[] = [];
    Object.values(rawPredictions || {}).forEach((arr) => {
      if (Array.isArray(arr)) allPreds.push(...arr);
    });
    if (allPreds.length === 0) {
      return { generatedAt: null, horizonHours: 0, hasNews: false };
    }
    // Latest generatedAt across all predictions in view.
    let latestGen: string | null = null;
    let latestTarget: string | null = null;
    let hasNews = false;
    for (const p of allPreds) {
      if (!latestGen || p.generatedAt > latestGen) latestGen = p.generatedAt;
      if (!latestTarget || p.targetTime > latestTarget) latestTarget = p.targetTime;
      if (p.type === 'news') hasNews = true;
    }
    let horizonHours = 0;
    if (latestGen && latestTarget) {
      horizonHours = Math.max(
        0,
        Math.round((new Date(latestTarget).getTime() - new Date(latestGen).getTime()) / 3_600_000),
      );
    }
    // rawPredictions always carries the full 30-day series (the server returns it
    // even for the 7d query — see the windowing comment below), so the raw span
    // would always read ~30 days. Cap it to the selected range so "Tidsram"
    // matches the 7d/30d toggle the user actually picked.
    const horizonCapHours = (isExtended ? 30 : 7) * 24;
    horizonHours = Math.min(horizonHours, horizonCapHours);
    return { generatedAt: latestGen, horizonHours, hasNews };
  }, [rawPredictions, isExtended]);

  const fmtRelative = useCallback((iso: string | null): string => {
    if (!iso) return '';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diffMs)) return '';
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return t('chart.justNow') || 'nu';
    if (mins < 60) return t('chart.minutesAgo', { count: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return t('chart.hoursAgo', { count: hrs });
    const days = Math.round(hrs / 24);
    return t('chart.daysAgo', { count: days });
  }, [t]);

  const selectedDate = selectedGeneratedAt ? selectedGeneratedAt.substring(0, 10) : '';
  const hoursForSelectedDate = useMemo(() => {
    return timestampsByDate.get(selectedDate) || [];
  }, [timestampsByDate, selectedDate]);

  // For the admin datepicker we restrict selectable days to the ones that
  // actually have a generation (includeDates). This is what stops a refetch on
  // mere month navigation: react-datepicker only fires onChange when a real day
  // is clicked, never when paging months, and disallowed days aren't clickable.
  const ymdToLocalDate = (ymd: string) => new Date(`${ymd}T00:00:00`);
  const localDateToYmd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const availableDateObjs = useMemo(
    () => Array.from(timestampsByDate.keys()).map(ymdToLocalDate),
    [timestampsByDate],
  );
  const selectedDateObj = selectedDate ? ymdToLocalDate(selectedDate) : null;

  // Reset zoom function
  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };


  const updateChartData = useCallback((filteredPredictions: Record<string, Prediction[]>) => {
    // Only create chart data if we have predictions
    // For commodities and stocks, use 'default' as the only area
    const isNotElectricity = predictionCategory !== 'electricity';
    const areasToInclude = isNotElectricity ? ['default'] : (selectedArea === 'ALL' ? AREA_CODES : [selectedArea]);

    // 30-day mode: the server already returns one daily point per (area, type)
    // for regression+news only, so we render it directly — no client-side
    // aggregation, no weather component. The hourly 7-day mode is unchanged.
    const showExtended = isExtended;

    // Determine if we have at least some predictions for any included area
    const hasAnyPredictions = areasToInclude.some(area => (filteredPredictions[area]?.length || 0) > 0);

    if (Object.keys(filteredPredictions).length > 0 && hasAnyPredictions) {
      // Group system predictions (weather, regression, news) by targetTime and type.
      // Components default to null (not 0) so the weighted blend can tell "model
      // produced no point here" apart from "model predicted exactly 0". This
      // matters for the daily (days 8–30) segment, which has regression + news
      // but no weather — see calculateWeightedPrediction* renormalization.
      // The single unified model writes one `type='unified'` point per timestamp,
      // so there's nothing to blend — we read `unified` directly. The legacy
      // weather/regression/news fields remain only as a transition fallback for
      // any old rows still in the DB; new generations only carry `unified`.
      const groupedPredictions: Record<string, Record<string, { weather: number | null; regression: number | null; news: number | null; unified: number | null; resolution?: 'hourly' | 'daily'; newsPredictionId?: string }>> = {};
      const newReasoningMap = new Map<string, { reasoning: string; predictionId: string }>();

      areasToInclude.forEach(area => {
        groupedPredictions[area] = {};

        filteredPredictions[area]?.forEach(prediction => {
          const targetTime = prediction.targetTime;

          if (!groupedPredictions[area][targetTime]) {
            groupedPredictions[area][targetTime] = { weather: null, regression: null, news: null, unified: null };
          }
          // Last writer wins for resolution; all components at a timestamp share it.
          groupedPredictions[area][targetTime].resolution = prediction.resolution ?? 'hourly';

          // Shadow models (multi-country preview, e.g. 'timestral-28m-wx') are plotted the same way.
          if (prediction.type === 'unified' || prediction.type?.startsWith('timestral')) {
            groupedPredictions[area][targetTime].unified = prediction.predictedValue;
            groupedPredictions[area][targetTime].newsPredictionId = prediction.id;
            if (prediction.reasoning) {
              newReasoningMap.set(prediction.id, { reasoning: prediction.reasoning, predictionId: prediction.id });
            }
          } else if (prediction.type === 'weather') {
            groupedPredictions[area][targetTime].weather = prediction.predictedValue;
          } else if (prediction.type === 'regression') {
            groupedPredictions[area][targetTime].regression = prediction.predictedValue;
          } else if (prediction.type === 'news') {
            groupedPredictions[area][targetTime].news = prediction.predictedValue;
            groupedPredictions[area][targetTime].newsPredictionId = prediction.id;
            if (prediction.reasoning) {
              newReasoningMap.set(prediction.id, { reasoning: prediction.reasoning, predictionId: prediction.id });
            }
          }
        });
      });
      
      // Get unique target times from all included areas
      let uniqueTargetTimes = Array.from(new Set(
        areasToInclude.flatMap(area => filteredPredictions[area]?.map(p => p.targetTime) || [])
      )).sort();

      // Window: some actual-price history + the full forecast horizon.
      //   The 30-day view is a uniform 4-hourly series (the server buckets the
      //   native-hourly days 0–7 down to 4-hourly and days 8–30 are already
      //   4-hourly). FORECAST_HORIZON_DAYS bounds the visible forward window.
      //   History scales with the horizon: 2 days back for the 7-day hourly view,
      //   7 days back for the 30-day daily view (collapsed to daily means below so
      //   the history cadence matches the forward forecast).
      //   Default: anchor to the latest actual price (consistent with welcome chart).
      //   Debug mode looking at an older generation: anchor to the prediction range
      //   itself so the full forecast horizon is visible, with the same history
      //   window leading in for comparison.
      const HOUR_MS = 60 * 60 * 1000;
      const FORECAST_HORIZON_DAYS = showExtended ? 30 : 7;
      const HISTORY_DAYS_BACK = showExtended ? 7 : 2;
      const globalActualMs = areasToInclude
        .flatMap(area => (prices[area] || []).map(p => new Date(p.timestamp).getTime()));
      const latestActualMs = globalActualMs.length > 0 ? Math.max(...globalActualMs) : Date.now();
      const predTimes = uniqueTargetTimes.map(ts => new Date(ts).getTime());
      let windowStartMs: number;
      let windowEndMs: number;
      if (timeTravel && predTimes.length > 0) {
        // Validation: show exactly the selected forecast interval, with NO history
        // lead-in. Anchor at the first predicted timestamp (forecast start) and
        // span the chosen horizon (7 or 30 days). We can't end at maxPred because
        // every prediction row — including the 8–30 day tail — is stored
        // resolution='hourly', so even the 7d server query returns the full
        // 30-day series; bounding by horizon here clips it to the chosen window.
        // Actual prices are filtered to this same window below, so predicted vs
        // actual line up for comparison.
        const minPred = Math.min(...predTimes);
        windowStartMs = minPred;
        windowEndMs = minPred + FORECAST_HORIZON_DAYS * 24 * HOUR_MS;
      } else {
        windowStartMs = latestActualMs - HISTORY_DAYS_BACK * 24 * HOUR_MS;
        windowEndMs = latestActualMs + FORECAST_HORIZON_DAYS * 24 * HOUR_MS;
      }

      // Filter actual prices to the window. In 30-day mode collapse them to
      // 4-hourly means so the history line matches the 4-hourly forecast cadence
      // and the category axis stays uniform (no hourly-vs-4-hourly compression).
      const filteredPricesData: Record<string, { timestamp: string; value: number }[]> = {};
      areasToInclude.forEach(area => {
        if (prices[area]) {
          const windowed = prices[area]
            .filter(price => {
              const t = new Date(price.timestamp).getTime();
              return t >= windowStartMs && t <= windowEndMs;
            })
            .map(price => ({ timestamp: price.timestamp, value: price.value }));
          filteredPricesData[area] = showExtended ? aggregatePricesTo4Hourly(windowed) : windowed;
        } else {
          filteredPricesData[area] = [];
        }
      });

      // Clip prediction times to the window as well (only forward of anchor matters)
      uniqueTargetTimes = uniqueTargetTimes.filter(ts => {
        const t = new Date(ts).getTime();
        return t >= windowStartMs && t <= windowEndMs;
      });

      // Compute last actual price timestamp across all included areas — predictions before
      // this point are hidden (day-ahead spot is already known, no need to forecast).
      const allActualMs = areasToInclude
        .flatMap(area => filteredPricesData[area]?.map(p => new Date(p.timestamp).getTime()) || []);
      const lastActualMs = allActualMs.length > 0 ? Math.max(...allActualMs) : -Infinity;

      // Combine all timestamps for x-axis (only prediction times and matching actual prices)
      const allTimestamps = Array.from(new Set([
        ...areasToInclude.flatMap(area => filteredPricesData[area]?.map(p => p.timestamp) || []),
        ...uniqueTargetTimes
      ])).sort();

      // Per-timestamp resolution lookup (hourly vs daily) so the tooltip and
      // x-axis labels can drop the hour component for daily points (legacy rows
      // only — the current 30-day tail is 4-hourly, tagged 'hourly', so its hour
      // is kept). A timestamp with no prediction (pure actual price) is hourly.
      const resolutionByTimestamp = new Map<string, 'hourly' | 'daily'>();
      areasToInclude.forEach(area => {
        const grouped = groupedPredictions[area];
        if (!grouped) return;
        Object.keys(grouped).forEach(ts => {
          if (grouped[ts].resolution === 'daily') resolutionByTimestamp.set(ts, 'daily');
          else if (!resolutionByTimestamp.has(ts)) resolutionByTimestamp.set(ts, 'hourly');
        });
      });
      const isDailyTimestamp = (ts: string | undefined): boolean =>
        ts ? resolutionByTimestamp.get(ts) === 'daily' : false;

      // Place the "forecast starts here" vertical line at the latest actual price
      // (default user view): for electricity, the day-ahead spot is known ~24h into
      // the future, so forecasts only start beyond that point. In validation mode,
      // place the line at the current wall-clock hour so admins can see where the
      // actuals stop for a still-maturing forecast.
      // Anchor the line to the current hour (validation) or the last known spot
      // price (default). In the 30-day daily view the axis only has noon-UTC
      // points, so an exact match rarely exists — snap to the last timestamp at or
      // before the anchor instead of requiring equality (otherwise the line
      // disappears).
      let nowIndex = -1;
      let anchorMs = -Infinity;
      if (timeTravel) {
        const now = new Date();
        anchorMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()).getTime();
      } else if (lastActualMs > -Infinity) {
        anchorMs = lastActualMs;
      }
      if (anchorMs > -Infinity) {
        // A fully matured past forecast (validation mode) has 'now' beyond the
        // whole window — don't pin the now-line to the right edge, just omit it.
        const lastTs = allTimestamps.length
          ? new Date(allTimestamps[allTimestamps.length - 1]).getTime()
          : -Infinity;
        if (!(timeTravel && anchorMs > lastTs)) {
          for (let i = allTimestamps.length - 1; i >= 0; i--) {
            if (new Date(allTimestamps[i]).getTime() <= anchorMs) { nowIndex = i; break; }
          }
        }
      }

      // Create datasets array starting with actual prices
      const datasets: any[] = [];

      // Mobile-specific sizing for chart elements
      const mobile = isMobile();
      const lineWidth = mobile ? 1.8 : 2.2;
      const pointRadiusHover = mobile ? 4 : 5;

      // Add actual prices datasets — solid clean line, no dots until hover
      areasToInclude.forEach((area) => {
        const isNotElectricity = predictionCategory !== 'electricity';
        if (filteredPricesData[area] && filteredPricesData[area].length > 0) {
          // Create a map of timestamp to value for easy lookup
          const priceMap = new Map(filteredPricesData[area].map(p => [p.timestamp, p.value]));

          const baseColor = isNotElectricity ? getCommodityColor(commodity!, darkMode) : areaColor(area, darkMode);
          const futureColor = lightenRgba(baseColor, 1.3);
          const label = isNotElectricity ? t('chart.actualPrices') : `${area} (${t('chart.actualPrices')})`;

          datasets.push({
            label: label,
            data: allTimestamps.map(timestamp => priceMap.get(timestamp) ?? null),
            borderColor: baseColor,
            backgroundColor: baseColor,
            borderWidth: lineWidth,
            tension: 0.35,
            ...(isNotElectricity && { spanGaps: true }),
            pointRadius: 0,
            pointHoverRadius: pointRadiusHover,
            pointBackgroundColor: baseColor,
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            segment: {
              borderColor: (ctx: any) => {
                // If we have a nowIndex and this segment is after it, use lighter color
                if (nowIndex >= 0 && ctx.p0DataIndex >= nowIndex) {
                  return futureColor;
                }
                return undefined; // Use default
              },
            },
          });
        }
      });

      // Add prediction datasets (solid lines) - darker variant of actual color
      areasToInclude.forEach((area) => {
        // Create a map of targetTime to weighted prediction value and track reasoning by prediction ID
        const predictionMap = new Map<string, number>();
        const predictionIdMap = new Map<string, string>(); // Map timestamp to prediction ID
        const isNotElectricity = predictionCategory !== 'electricity';

        // Per-area last actual timestamp+value, used to anchor the forecast line
        // so it visually continues from where the spot price ends.
        const areaPrices = filteredPricesData[area] || [];
        let areaLastActualMs = -Infinity;
        let areaLastActualValue: number | undefined;
        let areaLastActualTimestamp: string | undefined;
        for (const p of areaPrices) {
          const t = new Date(p.timestamp).getTime();
          if (t > areaLastActualMs) {
            areaLastActualMs = t;
            areaLastActualValue = p.value;
            areaLastActualTimestamp = p.timestamp;
          }
        }
        // Anchor the forecast at the last actual value so the dotted line continues
        // seamlessly. Skipped in validation mode — admins want to see raw forecast
        // values overlapping the known day-ahead spot for quality comparison.
        if (!timeTravel && areaLastActualTimestamp && areaLastActualValue !== undefined) {
          predictionMap.set(areaLastActualTimestamp, areaLastActualValue);
        }

        uniqueTargetTimes.forEach(targetTime => {
          const entry = groupedPredictions[area]?.[targetTime];
          // Skip only when there's literally no prediction at all for this hour.
          // We must NOT skip on weightedValue <= 0 because Nordic spot prices
          // (especially SE1/SE2) are routinely zero or negative — filtering
          // those out causes the prediction line to vanish into gaps.
          if (!entry) return;
          const weatherValue = entry.weather;
          const regressionValue = entry.regression;
          const newsValue = entry.news;
          const newsPredictionId = entry.newsPredictionId;

          let weightedValue: number | null;
          if (entry.unified !== null && entry.unified !== undefined) {
            // Single unified model — its value is the forecast, no blend.
            weightedValue = entry.unified;
          } else if (isNotElectricity || showExtended) {
            // Transition fallback for legacy rows: commodities/stocks, and
            // electricity 30-day, used regression↔news.
            weightedValue = calculateWeightedPredictionCommodity(regressionValue, newsValue, sliderValue);
          } else {
            // Transition fallback for legacy rows: electricity 7-day blend.
            weightedValue = calculateWeightedPredictionElectricity(weatherValue, regressionValue, newsValue, triangleWeights);
          }

          // Null means no model produced a usable point for this timestamp — skip
          // it so the line bridges the gap instead of dropping to zero.
          if (weightedValue === null) return;
          predictionMap.set(targetTime, weightedValue);
          // Store the news prediction ID if we have one (for reasoning lookup)
          if (newsPredictionId) {
            predictionIdMap.set(targetTime, newsPredictionId);
          }
        });

        const baseColor = isNotElectricity ? getCommodityColor(commodity!, darkMode) : areaColor(area, darkMode);
        // In dark mode, lighten predictions instead of darkening (better contrast on dark bg)
        const predictionColor = darkMode ? lightenRgba(baseColor, 1.2) : darkenRgba(baseColor, 0.7);
        const futurePredictionColor = lightenRgba(predictionColor, 1.3);
        const label = isNotElectricity ? t('chart.forecast') : `${area} (${t('chart.forecast')})`;

        if (predictionMap.size > 0) {
          // Create arrays for prediction IDs and point styles
          const predictionIds = allTimestamps.map(timestamp => predictionIdMap.get(timestamp) || null);

          // Create point style arrays - stars for points with reasoning (have prediction ID), tiny circles for others
          const pointStyleArray = allTimestamps.map(timestamp => {
            const predId = predictionIdMap.get(timestamp);
            return predId && newReasoningMap.has(predId) ? 'star' : 'circle';
          });

          // Only show points where we have AI reasoning (stars). Hide the rest.
          const pointRadiusArray = allTimestamps.map(timestamp => {
            const predId = predictionIdMap.get(timestamp);
            return predId && newReasoningMap.has(predId) ? (mobile ? 5 : 7) : 0;
          });

          const pointHoverRadiusArray = allTimestamps.map(timestamp => {
            const predId = predictionIdMap.get(timestamp);
            return predId && newReasoningMap.has(predId) ? (mobile ? 7 : 9) : (mobile ? 3 : 4);
          });

          datasets.push({
            label: label,
            data: allTimestamps.map(timestamp => {
              // The customer view only shows forecasts beyond the last known spot.
              // The admin validation view shows the full forecast, including the
              // portion that overlaps already-known actuals (for comparison).
              if (!timeTravel && new Date(timestamp).getTime() < lastActualMs) return null;
              return predictionMap.get(timestamp) ?? null;
            }),
            borderColor: predictionColor,
            backgroundColor: predictionColor,
            borderWidth: mobile ? 2 : 2.5,
            borderDash: [4, 4],
            tension: 0.35,
            pointRadius: pointRadiusArray,
            pointHoverRadius: pointHoverRadiusArray,
            pointStyle: pointStyleArray,
            pointBackgroundColor: predictionColor,
            pointBorderColor: predictionColor,
            pointBorderWidth: mobile ? 0.5 : 1,
            segment: {
              borderColor: (ctx: any) => {
                // If we have a nowIndex and this segment is after it, use lighter color
                if (nowIndex >= 0 && ctx.p0DataIndex >= nowIndex) {
                  return futurePredictionColor;
                }
                return undefined; // Use default
              },
            },
            // Store metadata for click handling
            metadata: {
              area: area,
              timestamps: allTimestamps,
              predictionIds: predictionIds, // Store prediction IDs for reasoning lookup
            },
          });
        }
      });

      const predictionsChartData = {
        labels: allTimestamps.map((timestamp) => new Date(timestamp).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: SWEDISH_TZ })),
        datasets: datasets,
      };

      // Update the ref immediately so handlers can access the latest reasoning map
      reasoningMapRef.current = newReasoningMap;

      // Update chart options with dynamic title
      const rangeLabel = showExtended ? t('chart.range30d') : t('chart.range7d');
      const timeSpan = calculateTimeSpan(uniqueTargetTimes, language);
      const startDate = allTimestamps.length > 0 ? new Date(allTimestamps[0]).toLocaleDateString(locale, { timeZone: SWEDISH_TZ }) : "";
      const endDate = allTimestamps.length > 0 ? new Date(allTimestamps[allTimestamps.length - 1]).toLocaleDateString(locale, { timeZone: SWEDISH_TZ }) : "";
      
      // Theme-aware colors for canvas-rendered elements
      const textColor = darkMode ? 'rgba(209, 213, 219, 1)' : 'rgba(55, 65, 81, 1)';
      const gridColor = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

      const newChartOptions = {
        responsive: true,
        // Must stay false. With maintainAspectRatio:true, Chart.js derives the
        // canvas HEIGHT from its width; that changes page height, which toggles
        // the window scrollbar, which changes the width again — an infinite
        // resize→redraw loop that pegs a CPU core and flickers the chart on this
        // page (worst on resize). The container div below owns a fixed height
        // instead, so the canvas just fills it and never feeds back into layout.
        maintainAspectRatio: false,
        animation: {
          duration: 250, // Fast animations for hide/show
        },
        plugins: {
          legend: { display: false },
          title: {
            display: true,
            text: commodity === 'copper'
              ? t('chart.title.static.copper', { range: rangeLabel })
              : commodity === 'crude'
                ? t('chart.title.static.crude', { range: rangeLabel })
                : t('chart.title.dynamic', { range: rangeLabel, span: timeSpan, start: startDate, end: endDate }),
            font: {
              size: mobile ? 12 : 14,
            },
            color: textColor,
            padding: {
              top: mobile ? 5 : 10,
              bottom: mobile ? 10 : 20,
            },
          },
          forecastArea: {
            nowIndex: nowIndex,
          },
          forecastNowLine: {
            nowIndex: nowIndex,
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x' as const,
            },
            zoom: {
              wheel: {
                enabled: !mobile, // Disable wheel zoom on mobile
                modifierKey: 'ctrl',
              },
              pinch: {
                enabled: true, // Enable pinch zoom on touch devices
              },
              drag: {
                enabled: !mobile, // Disable drag zoom on mobile (interferes with touch)
                backgroundColor: 'rgba(225,225,225,0.3)',
                borderColor: 'rgba(225,225,225)',
                borderWidth: 1,
                threshold: 10,
              },
              // Lock zoom to the x-axis so changing weighting / panning never
              // accidentally crops the price range on the y-axis (which used
              // to make low prices appear hidden below the chart area).
              mode: 'x' as const,
            },
          },
          tooltip: {
            enabled: true,
            titleFont: {
              size: mobile ? 11 : 13,
            },
            bodyFont: {
              size: mobile ? 10 : 12,
            },
            padding: mobile ? 6 : 10,
            callbacks: {
              title: (items: any[]) => {
                if (!items.length) return "";
                const ts = allTimestamps[items[0].dataIndex];
                if (!ts) return items[0].label;
                // Daily-segment points (days 8–30) represent a whole day — show
                // just the date, no hour, so we don't imply false hourly precision.
                const daily = isDailyTimestamp(ts);
                return new Date(ts).toLocaleString(locale, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  timeZone: SWEDISH_TZ,
                  ...(daily ? {} : { hour: "2-digit", minute: "2-digit" }),
                });
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              // The 30-day view is a uniform 4-hourly series (~180 points). Cap
              // ticks so labels stay readable; the category scale auto-spaces them.
              maxTicksLimit: mobile ? 6 : Math.min(allTimestamps.length, 12), // Fewer ticks on mobile
              font: {
                weight: "bold" as const,
                size: mobile ? 9 : 12,
              },
              color: textColor,
              maxRotation: mobile ? 45 : 0, // Allow rotation on mobile
              minRotation: mobile ? 45 : 0,
            },
            grid: {
              color: gridColor,
            },
          },
          y: {
            // Add breathing room above and below the data so neither the
            // lowest actuals nor the tallest forecasts get clipped against
            // the chart frame when the weighting widget moves the
            // prediction line up or down.
            grace: '8%' as const,
            title: {
              display: !mobile,
              text: unit,
              color: textColor,
              font: { size: 11 },
            },
            ticks: {
              font: {
                size: mobile ? 10 : 12,
              },
              color: textColor,
            },
            grid: {
              color: gridColor,
            },
          },
        },
        interaction: {
          mode: 'nearest' as const, // Use nearest to better detect points
          intersect: false,
        },
        onHover: (event: any, activeElements: any[]) => {
          const chart = chartRef.current;
          if (!chart) return;

          if (activeElements && activeElements.length > 0) {
            const element = activeElements[0];
            const dataset = chart.data.datasets[element.datasetIndex];
            if (dataset.metadata) {
              const { predictionIds } = dataset.metadata;
              const predictionId = predictionIds?.[element.index];
              if (predictionId && reasoningMapRef.current.has(predictionId)) {
                event.native.target.style.cursor = 'pointer';
                return;
              }
            }
          }
          event.native.target.style.cursor = 'default';
        },
        onClick: handleChartClick,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
      };

      // Set all three states together to ensure chart renders with complete data
      setReasoningMap(newReasoningMap);
      setPredictionsData(predictionsChartData);
      setChartOptions(newChartOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea, prices, sliderValue, triangleWeights, t, locale, language, commodity, predictionCategory, darkMode, timeTravel, forecastRange, canViewExtended, unit]);

  useEffect(() => {
    // Since predictions are now pre-filtered by generatedAt from the server,
    // pass rawPredictions directly to updateChartData
    if (Object.keys(rawPredictions).length > 0) {
      updateChartData(rawPredictions);
    }
  }, [rawPredictions, updateChartData]);

  return (
    <>
      <Card className="shadow-card">
        <Card.Body>
          <Card.Title className="d-flex justify-content-between align-items-center">
            <span>{t(
              commodity === 'copper' ? 'chart.title.static.copper' :
              commodity === 'crude' ? 'chart.title.static.crude' :
              'chart.title.static',
              { range: isExtended ? t('chart.range30d') : t('chart.range7d') }
            )}</span>
          </Card.Title>
        {(loadingPredictions || loadingPrices || loadingTimestamps) ? (
          // Hold the loader until prices, the timestamp list AND predictions are
          // all ready — they resolve independently, and showing the chart after
          // only some arrive caused a flash of partial/stale data ("double
          // loading"). loadingTimestamps stays true through the predictions fetch,
          // so it also covers the gap between prices finishing and predictions
          // starting on an asset switch.
          // Loader kept at the chart's footprint (same min-height) so swapping in
          // the real chart doesn't jump the layout — that height jump was the
          // "flicker" seen when switching assets or on refresh.
          <div className="chart-loading d-flex justify-content-center align-items-center">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {reasoningMap.size > 0 && (
              <div className="alert alert-secondary mt-3 mb-3 py-2 px-3" role="alert">
                <small>
                  <Star style={{ width: '0.9em', height: '0.9em', verticalAlign: '-0.12em' }} />{' '}
                  {t('chart.reasoningHint')}
                </small>
              </div>
            )}

            {/* Mobile hint for pinch zoom */}
            <div className="d-md-none text-center mb-2">
              <small className="text-muted">{t('chart.pinchToZoom') || 'Pinch to zoom'}</small>
            </div>

            <div
              className="chart-container chart-fade-in"
              style={{
                position: 'relative',
                width: '100%',
                // Fixed height (not min-height) so the canvas can't drive page
                // layout — required now that maintainAspectRatio is false, and
                // what breaks the scrollbar resize loop. Tune these px if the
                // proportions feel off.
                height: isMobile() ? '300px' : '420px',
              }}
            >
              {predictionsData && chartOptions && <Line options={chartOptions} data={predictionsData} ref={chartRef} />}
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={resetZoom}
                  style={{ fontSize: '0.8rem' }}
                >
                  {t('chart.resetZoom')}
                </Button>
                {canViewExtended && (
                  <div className="forecast-range-toggle" role="group" aria-label={t('chart.forecastRange')}>
                    <button
                      type="button"
                      aria-pressed={forecastRange === '7d'}
                      className={`forecast-range-btn ${forecastRange === '7d' ? 'active' : ''}`}
                      onClick={() => { onForecastRangeChange('7d'); track('forecast_range', '7d'); }}
                    >
                      {t('chart.range7d')}
                    </button>
                    <button
                      type="button"
                      aria-pressed={forecastRange === '30d'}
                      className={`forecast-range-btn ${forecastRange === '30d' ? 'active' : ''}`}
                      onClick={() => { onForecastRangeChange('30d'); track('forecast_range', '30d'); }}
                    >
                      {t('chart.range30d')}
                    </button>
                  </div>
                )}
              </div>
              <small className="text-muted d-none d-md-block">
                {t('chart.zoomHint') || 'Ctrl+scroll or drag to zoom'}
              </small>
            </div>

            {!loadingPredictions && availableTimestamps.length > 0 && (
              <div className="chart-controls mt-4">
                {/* LEFT: forecast metadata (always shown) or admin datepicker */}
                {timeTravel ? (
                  <div className="chart-controls-section chart-controls-section--left">
                    <span className="chart-controls-section-label">{t('chart.predictionFrom')}</span>
                    <div className="forecast-datepicker">
                      <div className="forecast-datepicker-group">
                        <label>{t('chart.forecastDate')}</label>
                        <DatePicker
                          selected={selectedDateObj}
                          includeDates={availableDateObjs}
                          dateFormat="yyyy-MM-dd"
                          className="form-control form-control-sm"
                          onChange={(d: Date | null) => {
                            if (!d) return;
                            const hours = timestampsByDate.get(localDateToYmd(d));
                            if (hours && hours.length > 0) {
                              onGeneratedAtChange?.(hours[0]);
                            }
                          }}
                        />
                      </div>
                      <div className="forecast-datepicker-group">
                        <label>{t('chart.forecastHour')}</label>
                        <Form.Select
                          size="sm"
                          value={selectedGeneratedAt || ''}
                          onChange={(e) => onGeneratedAtChange?.(e.target.value)}
                        >
                          {hoursForSelectedDate.map((ts) => (
                            <option key={ts} value={ts}>
                              {new Date(ts).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: SWEDISH_TZ })}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="chart-controls-section chart-controls-section--left">
                    <span className="chart-controls-section-label">{t('chart.forecastDetails')}</span>
                    <div className="forecast-info">
                      <div className="forecast-info__row">
                        <span className="forecast-info__label">{t('chart.generated')}</span>
                        <span className="forecast-info__value">
                          {forecastStats.generatedAt
                            ? new Date(forecastStats.generatedAt).toLocaleString(locale, {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: SWEDISH_TZ,
                              })
                            : '—'}
                          {forecastStats.generatedAt && (
                            <span className="forecast-info__hint">
                              {' '}· {fmtRelative(forecastStats.generatedAt)}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="forecast-info__row">
                        <span className="forecast-info__label">{t('chart.horizon')}</span>
                        <span className="forecast-info__value">
                          {forecastStats.horizonHours > 0
                            ? (forecastStats.horizonHours >= 48
                                ? t('chart.daysAhead', { count: Math.round(forecastStats.horizonHours / 24) })
                                : t('chart.hoursAhead', { count: forecastStats.horizonHours }))
                            : '—'}
                        </span>
                      </div>
                      <div className="forecast-info__row">
                        <span className="forecast-info__label">{t('chart.methods')}</span>
                        <span className="forecast-info__value forecast-info__chips">
                          {/* Weather only contributes to the electricity 7-day
                              view; the 30-day daily horizon drops it (no skill
                              that far out), so don't advertise it there. */}
                          {commodity === 'electricity' && !isExtended && (
                            <span className="forecast-chip forecast-chip--weather" title={t('chart.weather')}>
                              {t('chart.weather')}
                            </span>
                          )}
                          <span className="forecast-chip forecast-chip--trend" title={t('chart.regression')}>
                            {t('chart.regression')}
                          </span>
                          {forecastStats.hasNews && (
                            <span className="forecast-chip forecast-chip--news" title={t('chart.news')}>
                              {t('chart.news')}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* MIDDLE: area buttons (electricity only). Only shown when the
                    chart owns its area state (uncontrolled — e.g. the admin
                    validation chart). On the main page the area selector lives at
                    the top of the page and is passed in via onAreaChange, so we
                    don't render a second picker here. */}
                {commodity === 'electricity' && !onAreaChange && (
                  <div className="chart-controls-section chart-controls-section--mid">
                    <span className="chart-controls-section-label">{t('chart.selectArea')}</span>
                    <AreaPicker selectedArea={selectedArea} onChange={setSelectedArea} />
                  </div>
                )}

                {/* The prediction-weighting control is gone: a single trained
                    model now produces the forecast, so there are no input weights
                    to hand-tune. */}
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
    
      {showReasoning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={closeReasoningModal}
        >
          <div
            style={{
              backgroundColor: 'var(--bs-card-bg)',
              borderRadius: '16px',
              padding: '0',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid var(--bs-border-color-translucent, rgba(0, 0, 0, 0.08))',
            }}>
              <h5 style={{ margin: 0, fontWeight: '600', fontSize: '1.1rem', color: 'var(--bs-body-color)' }}>
                {t('chart.predictionReasoning')}
              </h5>
              <button
                onClick={closeReasoningModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--bs-secondary)',
                  padding: '0 4px',
                  lineHeight: '1',
                  opacity: '0.7',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
              >
                ×
              </button>
            </div>
            
            {/* Loading state */}
            {!reasoningData && (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {/* Content */}
            {reasoningData && (
            <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
              {/* Header Info Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '16px',
                borderBottom: '1px solid var(--bs-border-color-translucent, rgba(0, 0, 0, 0.08))',
              }}>
                {/* Type Badge and Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '4px' }}>
                    {reasoningData.predictionType === 'news' ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(13, 202, 240, 0.15)',
                        color: 'var(--bs-info)',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                      }}>
                        <Cpu style={{ width: '0.9em', height: '0.9em', verticalAlign: '-0.12em' }} /> AI Analysis
                      </span>
                    ) : reasoningData.predictionType === 'weather' ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(25, 135, 84, 0.15)',
                        color: 'var(--bs-success)',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                      }}>
                        <Cloud style={{ width: '0.9em', height: '0.9em', verticalAlign: '-0.12em' }} /> Weather
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(13, 110, 253, 0.15)',
                        color: 'var(--bs-primary)',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                      }}>
                        <BarChart style={{ width: '0.9em', height: '0.9em', verticalAlign: '-0.12em' }} /> Statistical
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: 'var(--bs-secondary)',
                  }}>
                    {new Date(reasoningData.timestamp).toLocaleString(locale, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: SWEDISH_TZ,
                    })}
                    {reasoningData.generatedAt && (
                      <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                        • Generated {new Date(reasoningData.generatedAt).toLocaleString(locale, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: SWEDISH_TZ,
                        })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Value */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '1.75rem',
                    fontWeight: '700',
                    color: 'var(--bs-primary)',
                    lineHeight: '1',
                  }}>
                    {typeof reasoningData.predictedValue === 'number'
                      ? convert(reasoningData.predictedValue).toFixed(2)
                      : reasoningData.predictedValue}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: 'var(--bs-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginTop: '2px',
                  }}>
                    Predicted
                  </div>
                </div>
              </div>

              {/* Analysis */}
              <div style={{
                color: 'var(--bs-body-color)',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.9rem',
              }}>
                {reasoningData.text}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
  </>
  );
}

export { Chart };
